import { AlertTriangle } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { type TemplateVariableKey, TemplateVariableButtons } from './template-variables';

import { Textarea } from '~/components/ui/textarea';

const SMS_VARIABLES: TemplateVariableKey[] = [
  'applicantName',
  'jobTitle',
  'jobEmployer',
  'jobLocation',
  'inviteUrl'
];

const GSM7_BASIC = new Set<string>([
  '@',
  '£',
  '$',
  '¥',
  'è',
  'é',
  'ù',
  'ì',
  'ò',
  'Ç',
  '\n',
  'Ø',
  'ø',
  '\r',
  'Å',
  'å',
  'Δ',
  '_',
  'Φ',
  'Γ',
  'Λ',
  'Ω',
  'Π',
  'Ψ',
  'Σ',
  'Θ',
  'Ξ',
  'Æ',
  'æ',
  'ß',
  'É',
  ' ',
  '!',
  '"',
  '#',
  '¤',
  '%',
  '&',
  "'",
  '(',
  ')',
  '*',
  '+',
  ',',
  '-',
  '.',
  '/',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  ':',
  ';',
  '<',
  '=',
  '>',
  '?',
  '¡',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
  'Ä',
  'Ö',
  'Ñ',
  'Ü',
  '§',
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'n',
  'o',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
  'ä',
  'ö',
  'ñ',
  'ü',
  'à'
]);
const GSM7_EXTENDED = new Set<string>(['\f', '^', '{', '}', '\\', '[', '~', ']', '|', '€']);

const graphemeSegmenter = new Intl.Segmenter();

interface SmsInfo {
  charCount: number;
  segments: number;
  segmentSize: number;
  maxChars: number;
  isUnicode: boolean;
}

function getSmsInfo(text: string, maxSegments: number): SmsInfo {
  const chars = Array.from(graphemeSegmenter.segment(text), (s) => s.segment);
  const isUnicode = chars.some((c) => !GSM7_BASIC.has(c) && !GSM7_EXTENDED.has(c));

  if (isUnicode) {
    const charCount = text.length; // UTF-16 code units
    const single = 70;
    const multi = 67;
    const segmentSize = charCount <= single ? single : multi;
    const segments = charCount === 0 ? 1 : charCount <= single ? 1 : Math.ceil(charCount / multi);
    return { charCount, segments, segmentSize, maxChars: multi * maxSegments, isUnicode: true };
  }

  const charCount = chars.reduce((n, c) => n + (GSM7_EXTENDED.has(c) ? 2 : 1), 0);
  const single = 160;
  const multi = 153;
  const segmentSize = charCount <= single ? single : multi;
  const segments = charCount === 0 ? 1 : charCount <= single ? 1 : Math.ceil(charCount / multi);
  return { charCount, segments, segmentSize, maxChars: multi * maxSegments, isUnicode: false };
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  maxSegments?: number;
}

export function SmsEditor({ value, onChange, disabled, maxSegments = 5 }: Props): React.ReactNode {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { charCount, segments, segmentSize, maxChars, isUnicode } = getSmsInfo(value, maxSegments);

  function insertVariable(variable: string): void {
    const el = textareaRef.current;
    if (!el) {
      onChange(value + variable);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = value.slice(0, start) + variable + value.slice(end);
    onChange(next);
    // Restore cursor after React re-renders
    requestAnimationFrame(() => {
      el.selectionStart = start + variable.length;
      el.selectionEnd = start + variable.length;
      el.focus();
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          disabled={disabled}
          rows={8}
          className="resize-none"
        />
        <div className="mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
          <span>{t('communications.characters', { count: charCount, max: maxChars })}</span>
          <span>{t('communications.smsSegments', { count: segments, size: segmentSize })}</span>
        </div>
        {isUnicode && (
          <p className="mt-2 flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
            <AlertTriangle className="size-3.5 shrink-0" />
            {t('communications.smsUnicodeWarning')}
          </p>
        )}
      </div>
      <TemplateVariableButtons
        variables={SMS_VARIABLES}
        onInsert={insertVariable}
        disabled={disabled}
      />
    </div>
  );
}
